import styled from '@emotion/styled';
import VerticalFlex from '../../components/flex_layouts/VerticalFlex';
import React, { useEffect, useState } from 'react';
import { TableProvider } from '../../context/TableContext';
import TableHeader from './header/TableHeader';
import { read, utils } from 'xlsx';
import { Trial } from '../../models/Trial';
import TableBody from './body/TableBody';
import Pagination from './pagination/Pagination';
import { useAppContext } from '../../context/AppContext';
import axios from 'axios';
import Colors from '../../components/theme/colors';

const TableSection = ({ ...props }) => {
  const { selectedTabId, freeSearchText, lastTableUpdateTime, setLastTableUpdateTime } = useAppContext();
  const tableHeaderRef = React.useRef() as React.MutableRefObject<HTMLDivElement>;
  const [trials, setTrials] = useState<Trial[]>([]);
  const [trialsCategories, setTrialsCategories] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 450px)');

    const handleResize = () => {
      if (mediaQuery.matches) {
        // If the height is larger than 700px
        setItemsPerPage(12);
      } else {
        setItemsPerPage(4);
      }
    };

    // Initial check on component mount
    handleResize();

    // Attach the event listener for window resize
    window.addEventListener('resize', handleResize);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []); // Empty dependency array to ensure the effect runs only once on mount

  useEffect(() => {
    function excelDateToJSDate(serial: number) {
      const utcDays = Math.floor(serial - 25569);
      const utcValue = utcDays * 86400;
      const dateInfo = new Date(utcValue * 1000);

      const fractionalDay = serial - Math.floor(serial) + 0.0000001;

      let totalSeconds = Math.floor(86400 * fractionalDay);

      const seconds = totalSeconds % 60;

      totalSeconds -= seconds;

      const hours = Math.floor(totalSeconds / (60 * 60));
      const minutes = Math.floor(totalSeconds / 60) % 60;

      return new Date(dateInfo.getFullYear(), dateInfo.getMonth(), dateInfo.getDate(), hours, minutes, seconds);
    }

    const loadData = async () => {
      const excludeColumns = ['internalpriority', 'Last update date']; // Columns to exclude

      const response = await axios.get('/NSCLC_clinical_trials.xlsx', {
        responseType: 'arraybuffer',
      });

      const arrayBuffer = await response.data;

      const workbook = read(arrayBuffer, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      const jsonData = utils.sheet_to_json(sheet).map((row: any) => {
        const filteredRow: any = {};
        Object.keys(row).forEach((key: string) => {
          if (!excludeColumns.includes(key)) {
            filteredRow[key] = row[key];
          } else if (key === 'Last update date' && !lastTableUpdateTime) {
            setLastTableUpdateTime?.(
              excelDateToJSDate(row[key])?.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              }),
            );
          }
        });
        return filteredRow;
      });

      console.log(jsonData);
      setTrials(
        jsonData.map(
          ({
            drug,
            lineoftherapy,
            mechanismofaction,
            nctidentifier,
            pathway,
            trialname,
            trialphases,
            uslocations,
            studydocuments,
          }) => ({
            trialName: trialname,
            drug,
            mechanismOfAction: mechanismofaction,
            trialPhases: trialphases,
            usLocations: uslocations,
            lineOfTherapy: lineoftherapy,
            nctIdentifier: nctidentifier,
            pathWay: pathway,
            studyDocuments: studydocuments,
          }),
        ),
      );
    };

    loadData();
  }, [lastTableUpdateTime, setLastTableUpdateTime]);

  useEffect(() => {
    const newTrialCategories: string[] = [];
    for (const trial of trials) {
      for (const [key] of Object.entries(trial)) {
        if (!newTrialCategories.includes(key)) {
          newTrialCategories.push(key);
        }
      }
    }

    setTrialsCategories(newTrialCategories);
  }, [trials]);

  const getProviderValue = () => {
    return {
      trials,
      currentPage,
      setCurrentPage,
      trialsCategories,
    };
  };

  const handleGoToTableHeader = () => {
    tableHeaderRef.current.scrollIntoView(true);
  };

  const filteredTrials = trials.filter(({ pathWay }) => pathWay === selectedTabId);

  const secondFiltered = freeSearchText
    ? filteredTrials.filter((item) =>
        Object.values(item).some(
          (value) => typeof value === 'string' && value.toLowerCase().includes(freeSearchText.toLowerCase()),
        ),
      )
    : filteredTrials;

  return (
    <TableProvider value={getProviderValue()}>
      <LayoutRoot {...props}>
        <MainContainer ref={tableHeaderRef}>
          <TableHeader ref={tableHeaderRef} />
          <TableBody itemsPerPage={itemsPerPage} filteredTrials={secondFiltered} />
          <Pagination
            itemsPerPage={itemsPerPage}
            goToTableHeader={handleGoToTableHeader}
            filteredTrials={secondFiltered}
          />
        </MainContainer>
      </LayoutRoot>
    </TableProvider>
  );
};

export default TableSection;

const LayoutRoot = styled.section`
  margin-top: 50px;
  padding: 0 70px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1044px) {
    margin-top: 20px;
    padding: 0 0 0 16px;

    @media print {
      padding-left: 0px;
    }
  }

  @media print {
    margin-top: 8px;
  }
`;

const MainContainer = styled(VerticalFlex)`
  padding: 32px;
  gap: 20px;
  width: 100%;
  align-items: center;
  max-width: 1320px;
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 20px 100px 0 rgba(65, 132, 247, 0.1);

  @media (max-width: 1044px) {
    max-width: none;
    padding: 16px;
  }

  @media print {
    gap: 8px;
    box-shadow: none;
    padding: 0;
    border: 0;
    border-radius: 0;
  }

  @media (max-width: 450px) {
    padding-right: 0;
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }
`;
